import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Check, Pill, Plus, X } from 'lucide-react';
import { api } from '../lib/api';
import { PatientHeader } from './PatientLayout';
import { Calendar, type CalendarEvent } from '../components/Calendar';
import { AgendaModal, type PersonalEvent } from './AgendaModal';
import { MedicacionModal } from './MedicacionModal';
import { Badge, Button, Card, ErrorState, Spinner, cx } from '../components/ui';
import { formatDateLong, formatRelative, formatTime } from '../lib/format';

interface TreatmentResponse {
  /** Sus inscripciones activas. Con dos, el calendario las junta. */
  programs: Array<{
    enrollmentId: string;
    id: string;
    name: string;
    brand: string | null;
    enrolledAt: string;
    form: { id: string; displayName: string } | null;
  }>;
  events: Array<{
    id: string;
    type: string;
    title: string;
    cycleNumber: number | null;
    scheduledAt: string;
    status: string;
    location: string | null;
    /** De cual de sus tratamientos es. Solo se muestra si tiene mas de uno. */
    program: { id: string; name: string; brand: string | null } | null;
  }>;
  /** Lo que anotó el paciente. Va aparte de las dosis del programa. */
  personales: PersonalEvent[];
  medications: Array<{
    id: string;
    name: string;
    dose: string | null;
    times: string[];
    instructions: string | null;
  }>;
}

export function TreatmentPage() {
  const queryClient = useQueryClient();

  const [agendaAbierta, setAgendaAbierta] = useState(false);
  const [editando, setEditando] = useState<PersonalEvent | null>(null);
  const [medicacionAbierta, setMedicacionAbierta] = useState(false);
  const [medicacionEditando, setMedicacionEditando] = useState<TreatmentResponse['medications'][number] | null>(null);

  const treatment = useQuery({
    queryKey: ['me', 'treatment'],
    queryFn: () => api.get<TreatmentResponse>('/me/treatment'),
  });

  const logDose = useMutation({
    mutationFn: (input: { medicationId: string; scheduledFor: string; status: 'TAKEN' | 'SKIPPED' }) =>
      api.post(`/me/medications/${input.medicationId}/log`, {
        scheduledFor: input.scheduledFor,
        status: input.status,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'treatment'] }),
  });

  // El error primero: una consulta que falló tampoco tiene datos, así que con
  // la espera arriba la pantalla giraría para siempre sin decir qué pasó.
  if (treatment.isError) {
    return <ErrorState error={treatment.error} onRetry={() => treatment.refetch()} />;
  }
  if (treatment.isLoading || !treatment.data) return <Spinner />;

  const data = treatment.data!;
  const now = Date.now();

  const upcoming = data.events
    .filter((event) => new Date(event.scheduledAt).getTime() >= now && event.status === 'SCHEDULED')
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));

  /**
   * Los dos juegos en la misma grilla, distinguidos.
   * Las dosis las programa su equipo; lo de `own` lo anotó el paciente y se
   * dibuja con otro color. Verlos juntos es el punto —es su mes— pero
   * confundirlos haría creer que un turno que se anotó solo lo sabe su equipo.
   */
  const porIdPersonal = new Map(data.personales.map((evento) => [evento.id, evento]));
  const eventosDelCalendario: CalendarEvent[] = [
    ...data.events,
    ...data.personales.map((evento) => ({
      id: evento.id,
      title: evento.title,
      scheduledAt: evento.scheduledAt,
      status: evento.doneAt ? 'COMPLETED' : 'SCHEDULED',
      type: evento.kind,
      location: evento.location,
      own: true,
    })),
  ];


  return (
    <div className="max-w-lg lg:max-w-3xl mx-auto">
      {/* La pantalla es el calendario: cuándo le toca cada cosa. Antes se
          llamaba "Tu tratamiento" y prometía algo que no muestra —qué es, cómo
          funciona, para qué sirve— cuando lo que hay son fechas. */}
      <PatientHeader
        title="Tu calendario"
        subtitle="Cuándo te toca cada aplicación y cada control"
      />

      <div className="px-5 py-5 space-y-7">
        <section>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="font-semibold">Tu medicación de hoy</h2>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setMedicacionEditando(null);
                setMedicacionAbierta(true);
              }}
            >
              <Plus className="size-4" />
              Agregar
            </Button>
          </div>

          {/* Antes esta sección no existía si no había ninguna, y no había
              forma de cargar una: el modelo estaba y no lo escribía nadie. */}
          {data.medications.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-fg-muted">
                Anotá lo que tomás además de tu tratamiento y te lo recordamos a la hora que
                elijas.
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {data.medications.map((medication) =>
                (medication.times.length > 0 ? medication.times : ['—']).map((time) => (
                  <Card key={`${medication.id}-${time}`} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="size-10 rounded-md bg-primary-soft text-primary grid place-items-center shrink-0">
                        <Pill className="size-5" />
                      </div>
                      <div className="min-w-0 grow">
                        <button
                          type="button"
                          className="font-semibold text-[15px] text-left hover:text-primary"
                          onClick={() => {
                            setMedicacionEditando(medication);
                            setMedicacionAbierta(true);
                          }}
                        >
                          {medication.name}
                        </button>
                        <p className="text-sm text-fg-muted">
                          {medication.dose}
                          {time !== '—' && ` · ${time}`}
                        </p>
                        {medication.instructions && (
                          <p className="text-sm text-fg-muted mt-1.5 leading-snug">
                            {medication.instructions}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        fullWidth
                        loading={logDose.isPending}
                        onClick={() =>
                          logDose.mutate({
                            medicationId: medication.id,
                            scheduledFor: scheduledAtToday(time),
                            status: 'TAKEN',
                          })
                        }
                      >
                        <Check className="size-4" />
                        La tomé
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        fullWidth
                        onClick={() =>
                          logDose.mutate({
                            medicationId: medication.id,
                            scheduledFor: scheduledAtToday(time),
                            status: 'SKIPPED',
                          })
                        }
                      >
                        <X className="size-4" />
                        No la tomé
                      </Button>
                    </div>
                  </Card>
                )),
              )}
            </div>
          )}
        </section>

        {/* El mes en una grilla, no en una lista.
            Un paciente en tratamiento no piensa su mes como un listado: piensa
            "¿cuándo es la próxima?" y "¿me queda alguna esta semana?", y las dos
            se contestan de un vistazo mirando un calendario. */}
        <section>
          <h2 className="font-semibold mb-3">Tu calendario</h2>
          {/* El calendario se dibuja siempre, tenga turnos o no.
              Antes, sin ninguno agendado, en su lugar aparecía un cartel y el
              mes desaparecía: la pantalla que se abre para ver "en qué día
              estamos y qué viene" dejaba de mostrar los días. El aviso de que
              todavía no hay nada va debajo, sin tapar la grilla. */}
          <Card className="p-4">
            <Calendar
              events={eventosDelCalendario}
              programs={data.programs}
              onEventClick={(evento) => {
                const propio = porIdPersonal.get(evento.id);
                if (!propio) return;
                setEditando(propio);
                setAgendaAbierta(true);
              }}
            />

            {eventosDelCalendario.length === 0 && (
              <p className="text-sm text-fg-muted text-center mt-4 pt-4 border-t border-border">
                <CalendarDays className="size-4 inline-block mr-1.5 -mt-0.5" />
                Todavía no tenés nada agendado. Cuando tu equipo programe tu próxima aplicación la
                vas a ver acá, y mientras tanto podés anotar lo tuyo.
              </p>
            )}

            {/* Su agenda: lo que quiera acordarse. Va debajo del mes y no
                arriba, porque lo primero que viene a ver es cuándo le toca. */}
            <Button
              variant="outline"
              fullWidth
              className="mt-4"
              onClick={() => {
                setEditando(null);
                setAgendaAbierta(true);
              }}
            >
              <Plus className="size-4" />
              Agregar a mi calendario
            </Button>
          </Card>
        </section>

        {upcoming.length > 0 && (
          <section>
            {/* El próximo turno, aparte del calendario: es el dato que se viene
                a buscar y no debería depender de encontrar el día correcto. */}
            <h2 className="font-semibold mb-3">Lo que viene</h2>
            <div className="space-y-3">
              {upcoming.slice(0, 3).map((event) => (
                <EventCard key={event.id} event={event} highlight showTreatment={data.programs.length > 1} />
              ))}
            </div>
          </section>
        )}
      </div>

      <AgendaModal
        open={agendaAbierta}
        evento={editando}
        onClose={() => {
          setAgendaAbierta(false);
          setEditando(null);
        }}
      />

      <MedicacionModal
        open={medicacionAbierta}
        medicacion={medicacionEditando}
        onClose={() => {
          setMedicacionAbierta(false);
          setMedicacionEditando(null);
        }}
      />
    </div>
  );
}

function EventCard({
  event,
  highlight,
  showTreatment,
}: {
  event: TreatmentResponse['events'][number];
  highlight?: boolean;
  /** Con dos tratamientos activos, cada cita tiene que decir de cual es. */
  showTreatment?: boolean;
}) {
  const statusTone =
    event.status === 'COMPLETED' ? 'success' : event.status === 'MISSED' ? 'danger' : 'neutral';

  const statusLabel =
    event.status === 'COMPLETED'
      ? 'Realizado'
      : event.status === 'MISSED'
        ? 'No asistido'
        : event.status === 'CANCELLED'
          ? 'Cancelado'
          : 'Agendado';

  return (
    <Card className={cx('p-4', highlight && 'border-primary')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Con un solo tratamiento sobra: ya lo dice el encabezado. Con dos,
              "Ciclo 4" y "Aplicacion 10" sin mas contexto se confunden, y son
              de tratamientos distintos. */}
          {showTreatment && event.program && (
            <p className="text-xs font-medium text-primary">
              {event.program.brand ?? event.program.name}
            </p>
          )}
          <p className="font-semibold text-[15px]">{event.title}</p>
          <p className="text-sm text-fg-muted mt-0.5">
            {formatDateLong(event.scheduledAt)} · {formatTime(event.scheduledAt)}
          </p>
          {event.location && <p className="text-sm text-fg-muted mt-0.5">{event.location}</p>}
        </div>
        <div className="text-right shrink-0 space-y-1">
          <Badge tone={statusTone}>{statusLabel}</Badge>
          {highlight && (
            <p className="text-xs text-primary font-medium">{formatRelative(event.scheduledAt)}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Convierte "09:00" en un instante de hoy, que es lo que espera la API. */
function scheduledAtToday(time: string): string {
  const now = new Date();
  const [hours, minutes] = time === '—' ? [now.getHours(), 0] : time.split(':').map(Number);
  now.setHours(hours ?? 9, minutes ?? 0, 0, 0);
  return now.toISOString();
}
